/**
 * Order Workflow Configuration - Simplified 15-Status Workflow
 *
 * This file defines the workflow stages based on the new simplified workflow.
 * See DOCS/new-workflow.md for full documentation.
 *
 * KEY CONCEPTS:
 * - isParallel: true = this phase can run alongside other phases (doesn't block)
 * - blocksProgress: true = must complete before moving to next step in this phase
 */

import { OrderStatusId } from './order-statuses.config';

export interface WorkflowStep {
  statusId: OrderStatusId;
  when: string; // When this status appears
  trigger: string; // What triggers this status
  action: string; // What user needs to do
  responsible: string; // Who is responsible
  blocksProgress: boolean; // Does this block next steps?
}

export interface WorkflowPhase {
  phase: number;
  name: string;
  description: string;
  isParallel: boolean; // Can run in parallel with other phases?
  steps: WorkflowStep[];
}

/**
 * MORICHAL ORDER WORKFLOW - Simplified 15-Status Version
 *
 * MAIN WORKFLOW (Sequential):
 * Phase 1: Review (Order Entry) - REVIEW, ORDER_CREATED
 * Phase 2: Supplier Selection & Quoting - SUPPLIER_SELECTION, QUOTE_REQUESTED, QUOTE_RECEIVED, SET_MARGIN
 * Phase 3: Proforma & Negotiation - PROFORMA_GENERATED, PROFORMA_SENT, NEGOTIATION, ADJUST_RESEND, PROFORMA_RESENT
 * Phase 4: PO & Payment - PO_CREATED, LOG_PAYMENT
 * Phase 5: Production - PRODUCTION, PACKING_LIST_RECEIVED
 * Phase 6: Document Verification & Shipping - DOC_VERIFICATION, SHIPMENT
 * Phase 7: Delivery - DELIVERED
 *
 * TERMINAL STATES: ORDER_REJECTED, ORDER_CANCELLED
 */
export const ORDER_WORKFLOW: WorkflowPhase[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 1: REVIEW (Order Entry)
  // Order arrives and gets reviewed by user
  // ═══════════════════════════════════════════════════════════════════════════
  {
    phase: 1,
    name: 'Review',
    description: 'Order review and approval',
    isParallel: false,
    steps: [
      {
        statusId: 'REVIEW',
        when: 'Order arrives for review',
        trigger: 'New order email detected or order created',
        action: 'Review AI extraction, edit if needed, approve or reject',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'ORDER_CREATED',
        when: 'User approves the order',
        trigger: 'User clicks "Approve"',
        action: 'Proceed to supplier selection',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'ORDER_REJECTED',
        when: 'User rejects the order',
        trigger: 'User clicks "Reject"',
        action: 'Order closed - no further action',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 2: SUPPLIER SELECTION & QUOTING
  // Select suppliers, send RFQs, receive quotes, set margin
  // ═══════════════════════════════════════════════════════════════════════════
  {
    phase: 2,
    name: 'Supplier Selection & Quoting',
    description: 'Select suppliers and manage quote process',
    isParallel: false,
    steps: [
      {
        statusId: 'SUPPLIER_SELECTION',
        when: 'Order approved, ready to source',
        trigger: 'User approved order',
        action: 'Review AI supplier suggestions. Add/remove suppliers.',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'QUOTE_REQUESTED',
        when: 'RFQ emails sent to suppliers',
        trigger: 'User sends quote requests',
        action: 'Wait for quotes. Can send reminders.',
        responsible: 'Suppliers',
        blocksProgress: true,
      },
      {
        statusId: 'QUOTE_RECEIVED',
        when: 'Quotes received from suppliers',
        trigger: 'Supplier responds with quote',
        action: 'Review and compare quotes',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'SET_MARGIN',
        when: 'Quote selected',
        trigger: 'User picks winning supplier quote',
        action: 'Set profit margin for customer quote',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 3: PROFORMA & NEGOTIATION
  // Generate proforma, negotiate with customer if needed
  // ═══════════════════════════════════════════════════════════════════════════
  {
    phase: 3,
    name: 'Proforma & Negotiation',
    description: 'Generate and send proforma, handle customer negotiation',
    isParallel: false,
    steps: [
      {
        statusId: 'PROFORMA_GENERATED',
        when: 'Margin set',
        trigger: 'After margin entry',
        action: 'Generate proforma invoice document',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'PROFORMA_SENT',
        when: 'Proforma generated',
        trigger: 'PFI document ready',
        action: 'Send proforma to customer for approval',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'NEGOTIATION',
        when: 'Customer wants to negotiate',
        trigger: 'Customer requests changes or price adjustment',
        action: 'Discuss terms with customer',
        responsible: 'Morichal sales',
        blocksProgress: false,
      },
      {
        statusId: 'ADJUST_RESEND',
        when: 'Terms need adjustment',
        trigger: 'Negotiation requires proforma changes',
        action: 'Adjust proforma based on negotiation',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'PROFORMA_RESENT',
        when: 'Adjusted proforma ready',
        trigger: 'User finishes adjustments',
        action: 'Send updated proforma to customer',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 4: PO & PAYMENT
  // Create purchase order, log payments
  // ═══════════════════════════════════════════════════════════════════════════
  {
    phase: 4,
    name: 'PO & Payment',
    description: 'Create purchase order and track payments',
    isParallel: false,
    steps: [
      {
        statusId: 'PO_CREATED',
        when: 'Customer accepts proforma',
        trigger: 'Customer signs/approves proforma',
        action: 'Create purchase order for supplier',
        responsible: 'Morichal sales',
        blocksProgress: true,
      },
      {
        statusId: 'LOG_PAYMENT',
        when: 'Payment received',
        trigger: 'Customer makes payment (advance, partial, or full)',
        action: 'Record payment in system',
        responsible: 'Morichal finance',
        blocksProgress: false,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 5: PRODUCTION
  // Manufacturing and packing
  // ═══════════════════════════════════════════════════════════════════════════
  {
    phase: 5,
    name: 'Production',
    description: 'Manufacturing and packing',
    isParallel: false,
    steps: [
      {
        statusId: 'PRODUCTION',
        when: 'PO sent to supplier',
        trigger: 'Supplier confirms order',
        action: 'Track production progress',
        responsible: 'Supplier / Morichal ops',
        blocksProgress: true,
      },
      {
        statusId: 'PACKING_LIST_RECEIVED',
        when: 'Production complete',
        trigger: 'Supplier sends packing list',
        action: 'Review packing list details',
        responsible: 'Morichal ops',
        blocksProgress: true,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 6: DOCUMENT VERIFICATION & SHIPPING
  // Verify shipping documents and track shipment
  // ═══════════════════════════════════════════════════════════════════════════
  {
    phase: 6,
    name: 'Document Verification & Shipping',
    description: 'Verify documents and track shipment',
    isParallel: false,
    steps: [
      {
        statusId: 'DOC_VERIFICATION',
        when: 'Shipping documents received',
        trigger: 'BL, CO, certificates uploaded',
        action: 'Verify all documents are correct',
        responsible: 'Morichal ops',
        blocksProgress: true,
      },
      {
        statusId: 'SHIPMENT',
        when: 'Goods shipped',
        trigger: 'Vessel departs',
        action: 'Track shipment and update customer',
        responsible: 'Carrier / Morichal ops',
        blocksProgress: true,
      },
    ],
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 7: DELIVERY
  // Final delivery confirmation
  // ═══════════════════════════════════════════════════════════════════════════
  {
    phase: 7,
    name: 'Delivery',
    description: 'Final delivery and completion',
    isParallel: false,
    steps: [
      {
        statusId: 'DELIVERED',
        when: 'Goods delivered to customer',
        trigger: 'Delivery confirmation received',
        action: 'Close order - complete',
        responsible: 'Morichal ops',
        blocksProgress: true,
      },
    ],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// PIPELINE STAGES - Visual progress indicator on order cards
// Edit these to change the stages shown in the pipeline visualization
// ═══════════════════════════════════════════════════════════════════════════════

export interface PipelineStage {
  label: string; // Full label (e.g., "Review")
  shortLabel: string; // Short label for compact view (e.g., "REV")
  phase: number; // Which workflow phase this maps to
}

/**
 * Pipeline stages shown on order cards
 * These are the simplified visual stages for the main workflow
 *
 * 6-Stage Visual Workflow:
 * 1. Review - Order review and approval (phase 1)
 * 2. Suppliers - Supplier selection & quoting (phase 2)
 * 3. Quoting - Proforma & negotiation (phase 3-4)
 * 4. Production - Manufacturing (phase 5)
 * 5. Shipping - Documents & shipping (phase 6)
 * 6. Delivered - Final delivery (phase 7)
 */
export const PIPELINE_STAGES: PipelineStage[] = [
  { label: 'Review', shortLabel: 'REV', phase: 1 },
  { label: 'Suppliers', shortLabel: 'SUP', phase: 2 },
  { label: 'Quoting', shortLabel: 'QTE', phase: 3 },
  { label: 'Production', shortLabel: 'PROD', phase: 5 },
  { label: 'Shipping', shortLabel: 'SHIP', phase: 6 },
  { label: 'Delivered', shortLabel: 'DEL', phase: 7 },
];

/**
 * Temporary single-supplier mode. When false the Suppliers stage is hidden
 * from navigation and RFQ auto-opens for the assigned mill. Flip back to
 * true to restore multi-supplier selection without deleting the stage.
 */
export const MULTI_SUPPLIER_ENABLED = false;

export function isMultiSupplierEnabled(): boolean {
  return MULTI_SUPPLIER_ENABLED;
}

/**
 * Get pipeline stages for UI
 */
export function getPipelineStages(): PipelineStage[] {
  return PIPELINE_STAGES;
}

/**
 * Get workflow info for a specific status
 */
export function getWorkflowStep(statusId: OrderStatusId): WorkflowStep | undefined {
  for (const phase of ORDER_WORKFLOW) {
    const step = phase.steps.find((s) => s.statusId === statusId);
    if (step) return step;
  }
  return undefined;
}

/**
 * Get the phase a status belongs to
 */
export function getPhaseForStatus(statusId: OrderStatusId): WorkflowPhase | undefined {
  return ORDER_WORKFLOW.find((phase) => phase.steps.some((s) => s.statusId === statusId));
}

/**
 * Get all steps for a specific phase
 */
export function getPhaseSteps(phaseNumber: number): WorkflowStep[] {
  const phase = ORDER_WORKFLOW.find((p) => p.phase === phaseNumber);
  return phase?.steps || [];
}

/**
 * Check if a phase can run in parallel with others
 */
export function isParallelPhase(phaseNumber: number): boolean {
  const phase = ORDER_WORKFLOW.find((p) => p.phase === phaseNumber);
  return phase?.isParallel || false;
}
